import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import QRCode from 'qrcode';
import { Loader2, ChevronRight, Share2, FileText } from 'lucide-react';
import { formatDate } from '../utils/date';
import { curLabel } from '../utils/currency';
import { useT } from '../utils/translate';
import AktSverkaDocModal from '../components/AktSverkaDocModal';
import AktSverkaShareModal from '../components/AktSverkaShareModal';
import DatePicker from '../components/DatePicker';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const fmt = n => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

const pad = n => String(n).padStart(2, '0');
const ymd = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const today = () => ymd(new Date());
const yearStart = () => ymd(new Date(new Date().getFullYear(), 0, 1));

function BalanceSummary({ currency, opening, closing, t }) {
  const youOwe = closing > 0;
  const theyOwe = closing < 0;
  return (
    <div className="card p-4">
      <p className="text-xs font-medium text-ink-tertiary uppercase tracking-wide">{curLabel(currency)}</p>
      <p className={`text-2xl font-bold mt-1 ${youOwe ? 'text-red-600' : theyOwe ? 'text-primary-600' : 'text-ink'}`}>
        {fmt(Math.abs(closing))}
      </p>
      <p className="text-xs text-ink-tertiary mt-1">
        {youOwe ? t('aktSverka.youOwe') : theyOwe ? t('aktSverka.theyOwe') : t('aktSverka.noDebt')}
      </p>
      {opening !== 0 && (
        <p className="text-[11px] text-ink-tertiary mt-2 pt-2 border-t border-surface-100">
          {t('aktSverka.opening')}: {fmt(opening)} {curLabel(currency)}
        </p>
      )}
    </div>
  );
}

function TxRow({ row, currency, onOpen }) {
  return (
    <button onClick={() => onOpen(row)} className="w-full card-sm flex items-center justify-between gap-3 text-left hover:border-primary-300 transition-colors">
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="badge-gray text-[10px]">{row.kindLabel}</span>
          <span className="text-xs font-medium text-ink">№{row.number}</span>
        </div>
        <p className="text-xs text-ink-tertiary mt-1">{formatDate(row.date)}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <div className="text-right">
          {row.debit > 0 && <p className="text-sm font-semibold text-red-600">+{fmt(row.debit)}</p>}
          {row.credit > 0 && <p className="text-sm font-semibold text-primary-600">-{fmt(row.credit)}</p>}
          <p className="text-[11px] text-ink-tertiary mt-0.5">{fmt(row.balance)} {curLabel(currency)}</p>
        </div>
        <ChevronRight className="w-4 h-4 text-ink-tertiary" />
      </div>
    </button>
  );
}

export default function AktSverkaPage() {
  const t = useT();
  const slug = useSelector((s) => s.auth.customer?.organization?.slug);
  const [from, setFrom] = useState(yearStart());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openRow, setOpenRow] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [shareOpen, setShareOpen] = useState(false);

  // Doimiy public havola — admin panel bilan bir xil aktSverkaToken mexanizmi,
  // lekin ulashish sahifasi ("/akt-sverka/:token") admin frontend'da joylashgan
  // (client-app'da emas), shuning uchun URL o'sha loyihaning root domenidan quriladi.
  useEffect(() => {
    if (!slug) return;
    axios.get(`${API}/client/akt-sverka/token`).then(res => {
      const adminRoot = process.env.REACT_APP_ADMIN_ROOT_DOMAIN;
      if (!adminRoot) return;
      setShareUrl(`${window.location.protocol}//${slug}.${adminRoot}/akt-sverka/${res.data.token}`);
    }).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (!shareUrl) { setQrDataUrl(''); return; }
    let cancelled = false;
    QRCode.toDataURL(shareUrl, { width: 240, margin: 1 })
      .then(url => { if (!cancelled) setQrDataUrl(url); })
      .catch(() => { if (!cancelled) setQrDataUrl(''); });
    return () => { cancelled = true; };
  }, [shareUrl]);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    axios.get(`${API}/client/akt-sverka`, { params: { from, to } })
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.message || t('aktSverka.loadError')))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const currencies = data
    ? Array.from(new Set([...Object.keys(data.opening || {}), ...Object.keys(data.closing || {}), ...(data.rows || []).map(r => r.currency)]))
    : [];

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold text-ink">{t('aktSverka.title')}</h1>
        {data && shareUrl && (
          <div className="flex items-center gap-1.5">
            <a href={shareUrl} target="_blank" rel="noopener noreferrer" title={t('aktSverka.pdfDownload')}
              className="p-2 rounded-lg text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors">
              <FileText className="w-4 h-4" />
            </a>
            <button onClick={() => setShareOpen(true)} title={t('aktSverka.share')}
              className="p-2 rounded-lg text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors">
              <Share2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Balans — birinchi ko'rinadigan narsa */}
      {data && currencies.length > 0 && (
        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(currencies.length, 2)}, minmax(0,1fr))` }}>
          {currencies.map(cur => (
            <BalanceSummary key={cur} currency={cur} opening={data.opening?.[cur] || 0} closing={data.closing?.[cur] || 0} t={t} />
          ))}
        </div>
      )}

      {/* Davr — to'g'ridan-to'g'ri kalendar, default yil boshidan bugungacha */}
      <div className="card p-3 grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[11px] font-medium text-ink-tertiary mb-1">{t('aktSverka.fromDate')}</label>
          <DatePicker value={from} onChange={setFrom} disableFuture />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-ink-tertiary mb-1">{t('aktSverka.toDate')}</label>
          <DatePicker value={to} onChange={setTo} disableFuture minDate={from} />
        </div>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-ink-tertiary" />
        </div>
      ) : error ? (
        <div className="card-sm text-center text-sm text-red-600">{error}</div>
      ) : data && currencies.length === 0 ? (
        <div className="card-sm text-center text-sm text-ink-tertiary">{t('aktSverka.noTx')}</div>
      ) : data && (
        <div className="space-y-2">
          {(data.rows || []).map((r, i) => (
            <TxRow key={r._id || i} row={r} currency={r.currency} onOpen={setOpenRow} />
          ))}
        </div>
      )}

      {openRow && (
        <AktSverkaDocModal row={openRow} currency={openRow.currency} onClose={() => setOpenRow(null)} />
      )}

      {shareOpen && (
        <AktSverkaShareModal shareUrl={shareUrl} qrDataUrl={qrDataUrl} onClose={() => setShareOpen(false)} />
      )}
    </div>
  );
}
